import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  AlertTriangle, Plus, Car, Calendar, MapPin, User, Euro,
  Loader2, FileText, Edit2, Trash2, Upload, CheckCircle, Clock
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { formatCurrency, formatDate } from "../../../lib/fleet-constants";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CLAIM_TYPES = [
  { value: "accident", label: "Accident" },
  { value: "theft", label: "Vol" },
  { value: "vandalism", label: "Vandalisme" },
  { value: "glass_breakage", label: "Bris de glace" },
  { value: "fire", label: "Incendie" },
  { value: "natural_disaster", label: "Catastrophe naturelle" },
  { value: "other", label: "Autre" },
];

const RESPONSIBILITY_OPTIONS = [
  { value: "driver", label: "Conducteur" },
  { value: "third_party", label: "Tiers" },
  { value: "shared", label: "Partagée" },
  { value: "unknown", label: "Indéterminée" },
];

const STATUS_OPTIONS = [
  { value: "declared", label: "Déclaré", color: "bg-blue-100 text-blue-800" },
  { value: "in_progress", label: "En cours", color: "bg-amber-100 text-amber-800" },
  { value: "repair_scheduled", label: "Réparation planifiée", color: "bg-purple-100 text-purple-800" },
  { value: "repaired", label: "Réparé", color: "bg-green-100 text-green-800" },
  { value: "closed", label: "Clôturé", color: "bg-slate-100 text-slate-800" },
];

export default function ClaimsTab({ vehicleId, employees = [] }) {
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    claim_date: new Date().toISOString().split("T")[0],
    claim_type: "accident",
    description: "",
    location: "",
    responsibility: "unknown",
    police_report_number: "",
    insurance_claim_number: "",
    driver_employee_id: "",
    estimated_cost: "",
    deductible: "",
    repair_cost: "",
    repair_date: "",
    repair_supplier: "",
    status: "declared",
    notes: "",
  });
  
  const token = localStorage.getItem("token");

  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/fleet/vehicles/${vehicleId}/claims`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaims(response.data.claims || []);
    } catch (err) {
      console.error("Error fetching claims:", err);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [vehicleId, token]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const resetForm = () => {
    setForm({
      claim_date: new Date().toISOString().split("T")[0],
      claim_type: "accident",
      description: "",
      location: "",
      responsibility: "unknown",
      police_report_number: "",
      insurance_claim_number: "",
      driver_employee_id: "",
      estimated_cost: "",
      deductible: "",
      repair_cost: "",
      repair_date: "",
      repair_supplier: "",
      status: "declared",
      notes: "",
    });
    setEditingId(null);
  };

  const handleEdit = (claim) => {
    setForm({
      claim_date: claim.claim_date || "",
      claim_type: claim.claim_type || "accident",
      description: claim.description || "",
      location: claim.location || "",
      responsibility: claim.responsibility || "unknown",
      police_report_number: claim.police_report_number || "",
      insurance_claim_number: claim.insurance_claim_number || "",
      driver_employee_id: claim.driver_employee_id || "",
      estimated_cost: claim.estimated_cost || "",
      deductible: claim.deductible || "",
      repair_cost: claim.repair_cost || "",
      repair_date: claim.repair_date || "",
      repair_supplier: claim.repair_supplier || "",
      status: claim.status || "declared",
      notes: claim.notes || "",
    });
    setEditingId(claim.id);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.description) {
      toast.error("Description requise");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        estimated_cost: form.estimated_cost ? parseFloat(form.estimated_cost) : null,
        deductible: form.deductible ? parseFloat(form.deductible) : null,
        repair_cost: form.repair_cost ? parseFloat(form.repair_cost) : null,
        driver_employee_id: form.driver_employee_id || null,
      };
      
      if (editingId) {
        await axios.put(
          `${API}/fleet/vehicles/${vehicleId}/claims/${editingId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Sinistre mis à jour");
      } else {
        await axios.post(
          `${API}/fleet/vehicles/${vehicleId}/claims`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Sinistre déclaré");
      }
      
      setShowModal(false);
      resetForm();
      fetchClaims();
    } catch (err) {
      console.error("Error saving claim:", err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (claimId) => {
    if (!window.confirm("Supprimer ce sinistre ?")) return;
    
    try {
      await axios.delete(
        `${API}/fleet/vehicles/${vehicleId}/claims/${claimId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Sinistre supprimé");
      fetchClaims();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status);
    return opt ? (
      <Badge className={opt.color}>{opt.label}</Badge>
    ) : (
      <Badge variant="outline">{status}</Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const openClaims = claims.filter(c => !["repaired", "closed"].includes(c.status));
  const closedClaims = claims.filter(c => ["repaired", "closed"].includes(c.status));
  const totalCost = claims.reduce((sum, c) => sum + (c.repair_cost || c.deductible || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Sinistres</h3>
          <p className="text-sm text-slate-500">
            Déclarations, suivi et réparations
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} data-testid="add-claim-btn">
          <Plus className="w-4 h-4 mr-2" />
          Déclarer un sinistre
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total sinistres</p>
                <p className="text-2xl font-bold text-slate-900">{claims.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">En cours</p>
                <p className="text-2xl font-bold text-amber-600">{openClaims.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Résolus</p>
                <p className="text-2xl font-bold text-green-600">{closedClaims.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Coût total</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(totalCost)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Euro className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Claims Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Historique des sinistres</CardTitle>
        </CardHeader>
        <CardContent>
          {claims.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <AlertTriangle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p>Aucun sinistre déclaré</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Responsabilité</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Coût</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {claims.map((claim) => (
                  <TableRow key={claim.id}>
                    <TableCell>{formatDate(claim.claim_date)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {CLAIM_TYPES.find(t => t.value === claim.claim_type)?.label || claim.claim_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {claim.description}
                    </TableCell>
                    <TableCell>{claim.responsibility_label}</TableCell>
                    <TableCell>{getStatusBadge(claim.status)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {claim.repair_cost ? formatCurrency(claim.repair_cost) : 
                       claim.deductible ? formatCurrency(claim.deductible) : "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(claim)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(claim.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifier le sinistre" : "Déclarer un sinistre"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date du sinistre *</Label>
                <Input
                  type="date"
                  value={form.claim_date}
                  onChange={(e) => setForm({ ...form, claim_date: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select
                  value={form.claim_type}
                  onValueChange={(v) => setForm({ ...form, claim_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLAIM_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Responsabilité</Label>
                <Select
                  value={form.responsibility}
                  onValueChange={(v) => setForm({ ...form, responsibility: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RESPONSIBILITY_OPTIONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                placeholder="Décrivez les circonstances du sinistre..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lieu</Label>
                <Input
                  placeholder="Ex: Parking du siège"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Conducteur</Label>
                <Select
                  value={form.driver_employee_id || "none"}
                  onValueChange={(v) => setForm({ ...form, driver_employee_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non renseigné</SelectItem>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>N° PV police</Label>
                <Input
                  placeholder="Ex: 2024/123456"
                  value={form.police_report_number}
                  onChange={(e) => setForm({ ...form, police_report_number: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>N° déclaration assurance</Label>
                <Input
                  placeholder="Ex: SIN-2024-001"
                  value={form.insurance_claim_number}
                  onChange={(e) => setForm({ ...form, insurance_claim_number: e.target.value })}
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">Coûts</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Estimation</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.estimated_cost}
                    onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Franchise</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.deductible}
                    onChange={(e) => setForm({ ...form, deductible: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Coût réparation</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.repair_cost}
                    onChange={(e) => setForm({ ...form, repair_cost: e.target.value })}
                  />
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">Réparation</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Date réparation</Label>
                  <Input
                    type="date"
                    value={form.repair_date}
                    onChange={(e) => setForm({ ...form, repair_date: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Réparateur</Label>
                  <Input
                    placeholder="Ex: Carrosserie Dupont"
                    value={form.repair_supplier}
                    onChange={(e) => setForm({ ...form, repair_supplier: e.target.value })}
                  />
                </div>
                
                {editingId && (
                  <div className="space-y-2">
                    <Label>Statut</Label>
                    <Select
                      value={form.status}
                      onValueChange={(v) => setForm({ ...form, status: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Notes complémentaires..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : editingId ? (
                <Edit2 className="w-4 h-4 mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              {editingId ? "Mettre à jour" : "Déclarer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
